import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Coins } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUsage } from '../hooks/useUsage';
import { useAnonymousCredits } from '../hooks/useAnonymousCredits';
import { SlotCounter } from './SlotCounter';
import { PAYMENTS_DISABLED } from '../lib/payments';

interface CreditBalanceBadgeProps {
  className?: string;
}

export function CreditBalanceBadge({ className = '' }: CreditBalanceBadgeProps) {
  const { user } = useAuth();
  const { data } = useUsage();
  const { credits: anonymousCredits } = useAnonymousCredits();

  const resolvedCredits = user
    ? (data?.credits?.totalRemaining ?? null)
    : anonymousCredits;

  // Keep the last known value visible during transient refetch gaps so the
  // slot counter stays mounted and can animate when new values arrive.
  const [displayCredits, setDisplayCredits] = useState<number | null>(resolvedCredits);

  useEffect(() => {
    if (resolvedCredits !== null) {
      setDisplayCredits(resolvedCredits);
    }
  }, [resolvedCredits]);

  if (displayCredits === null) return null;

  const lowThreshold = user
    ? ((data?.costs?.sponsoredAiCall ?? 8) * 3)
    : 12;
  const outThreshold = user
    ? (data?.costs?.builderAiPer30Sec ?? 4)
    : 4;

  const isLow = displayCredits < lowThreshold;
  const isOut = displayCredits < outThreshold;
  const title = user ? 'Credits remaining' : 'Free credits remaining today';
  const classNames = `flex items-center gap-1.5 text-sm font-medium transition-colors ${
    isOut ? 'text-theme-error' : isLow ? 'text-amber-500' : 'text-theme-text-muted'
  } ${className}`;

  if (PAYMENTS_DISABLED) {
    return (
      <span className={classNames} title={title}>
        <Coins className="h-4 w-4" />
        <SlotCounter value={displayCredits} />
      </span>
    );
  }

  return (
    <Link
      to="/pricing"
      className={`${classNames} hover:opacity-80`}
      title={title}
    >
      <Coins className="h-4 w-4" />
      <SlotCounter value={displayCredits} />
    </Link>
  );
}
